import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Stack,
  Button,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from '../../api/axiosConfig';
import EmptyState from '../common/EmptyState';
import BusIcon from '../../assets/icons/bus.png';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';

const busIcon = L.icon({
  iconUrl: BusIcon,
  iconSize: [38, 38],
  iconAnchor: [19, 38],
  popupAnchor: [0, -38],
});

const DEFAULT_CENTER = [20.5937, 78.9629];
const DEFAULT_ZOOM = 5;
const PanelCard = styled(AdminPanelCard)(({ theme }) => ({
  borderRadius: 6,
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const FlyToLiveLocation = ({ location }) => {
  const map = useMap();

  useEffect(() => {
    if (!location) return;
    map.flyTo([location.latitude, location.longitude], 14, { duration: 0.9 });
  }, [location, map]);

  return null;
};

const MapTrack = () => {
  const theme = useTheme();
  const schoolId = localStorage.getItem('schoolId');
  const [buses, setBuses] = useState([]);
  const [selectedBusId, setSelectedBusId] = useState('');
  const [locationData, setLocationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingBuses, setLoadingBuses] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const selectedBus = useMemo(
    () => buses.find((bus) => String(bus.busId) === String(selectedBusId)) || null,
    [buses, selectedBusId]
  );

  const fetchBuses = useCallback(async () => {
    setLoadingBuses(true);
    setError('');
    try {
      const response = await axios.get('/api/buses', {
        headers: schoolId ? { schoolId } : undefined,
      });
      const busList = Array.isArray(response.data) ? response.data : [];
      setBuses(busList);

      if (busList.length > 0) {
        setSelectedBusId((prev) => prev || String(busList[0].busId));
      } else {
        setSelectedBusId('');
        setLocationData(null);
      }
    } catch (err) {
      setError('Unable to load buses.');
      showSnackbar('Unable to load buses.', 'error');
    } finally {
      setLoadingBuses(false);
    }
  }, [schoolId, showSnackbar]);

  const fetchSelectedLocation = useCallback(
    async (busId) => {
      const bus = buses.find((item) => String(item.busId) === String(busId));
      if (!bus) return;

      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`/api/locations/bus/${encodeURIComponent(busId)}`);
        const payload = response.data;

        if (!payload || payload.latitude == null || payload.longitude == null) {
          setLocationData(null);
          setError('No live location found for the selected bus.');
          return;
        }

        setLocationData({
          gpsDeviceId: payload.gpsDeviceId || bus.gpsDeviceId || null,
          source: payload.source || null,
          ageSeconds: payload.ageSeconds ?? null,
          stale: Boolean(payload.stale),
          latitude: Number(payload.latitude),
          longitude: Number(payload.longitude),
          timestamp: payload.timestamp || null,
        });
      } catch (err) {
        setLocationData(null);
        if (err?.response?.status === 404) {
          setError('No live location found for the selected bus.');
          return;
        }
        setError('Unable to fetch bus location.');
        showSnackbar('Unable to fetch bus location.', 'error');
      } finally {
        setLoading(false);
      }
    },
    [buses, showSnackbar]
  );

  useEffect(() => {
    fetchBuses();
  }, [fetchBuses]);

  useEffect(() => {
    if (!selectedBusId) return;
    fetchSelectedLocation(selectedBusId);
  }, [selectedBusId, fetchSelectedLocation]);

  const handleBusChange = (event) => {
    setSelectedBusId(event.target.value);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <AdminPageHero
        title="Live Bus Tracking"
        subtitle="Monitor the latest bus location and review GPS status from one map view."
        metrics={[
          { label: 'Buses', value: buses.length },
          { label: 'Tracking', value: selectedBusId ? 1 : 0 },
        ]}
      />

      <Box sx={{ mt: 3 }}>
        <PanelCard
          title="Map Tracker"
          subtitle="Choose a bus, refresh its live location, and view signal freshness."
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 } }}>
                <InputLabel id="bus-select-label">Select Bus</InputLabel>
                <Select
                  labelId="bus-select-label"
                  label="Select Bus"
                  value={selectedBusId}
                  onChange={handleBusChange}
                  disabled={loadingBuses || buses.length === 0}
                >
                  {buses.map((bus) => (
                    <MenuItem key={bus.busId} value={String(bus.busId)}>
                      {bus.busNumber || `Bus #${bus.busId}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                onClick={() => selectedBusId && fetchSelectedLocation(selectedBusId)}
                disabled={!selectedBusId || loading}
              >
                Refresh Location
              </Button>
              {locationData ? (
                <Chip
                  size="small"
                  label={locationData.stale ? 'Signal stale' : 'Signal live'}
                  sx={{
                    bgcolor: locationData.stale
                      ? alpha(theme.palette.warning.main, 0.12)
                      : alpha(theme.palette.success.main, 0.12),
                    color: locationData.stale
                      ? theme.palette.warning.main
                      : theme.palette.success.main,
                    fontWeight: 700,
                  }}
                />
              ) : null}
            </Stack>

            {error ? <Alert severity="warning">{error}</Alert> : null}

            {buses.length === 0 && !loadingBuses ? (
              <EmptyState
                title="No buses available"
                description="Add at least one bus to start map tracking."
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 520,
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                }}
              >
                <MapContainer
                  center={
                    locationData
                      ? [locationData.latitude, locationData.longitude]
                      : DEFAULT_CENTER
                  }
                  zoom={locationData ? 14 : DEFAULT_ZOOM}
                  style={{ width: '100%', height: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {locationData ? <FlyToLiveLocation location={locationData} /> : null}
                  <Marker
                    position={
                      locationData
                        ? [locationData.latitude, locationData.longitude]
                        : DEFAULT_CENTER
                    }
                    icon={busIcon}
                  >
                    <Popup>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {selectedBus?.busNumber || 'Bus Location'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Device: {locationData?.gpsDeviceId || selectedBus?.gpsDeviceId || 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Source: {locationData?.source || 'N/A'}
                      </Typography>
                      {locationData?.ageSeconds != null ? (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          Last update: {locationData.ageSeconds}s ago{locationData.stale ? ' (stale)' : ''}
                        </Typography>
                      ) : null}
                      {locationData ? (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          [{locationData.latitude.toFixed(6)}, {locationData.longitude.toFixed(6)}]
                        </Typography>
                      ) : (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          Live location not available yet.
                        </Typography>
                      )}
                    </Popup>
                  </Marker>
                </MapContainer>
              </Box>
            )}
          </Stack>
        </PanelCard>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MapTrack;
